<template>
  <Card v-if="needsChecklist" size="sm" class="onboarding-banner">
    <div class="onboarding-banner__content">
      <div class="onboarding-banner__text">
        <p class="onboarding-banner__title">{{ $t('onboarding.checklist.banner.title') }}</p>
        <p class="onboarding-banner__subtitle">
          {{ $t('onboarding.checklist.banner.subtitle', { done: completedCount, total }) }}
        </p>
        <div class="onboarding-banner__progress">
          <div
            class="onboarding-banner__progress-fill"
            :style="{ width: `${progressPercent}%` }"
          />
        </div>
      </div>
      <Btn
        variant="primary"
        size="sm"
        :text="$t('onboarding.checklist.banner.cta')"
        @click="handleOpen"
      />
    </div>
  </Card>
</template>

<script>
  import { computed, onMounted, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import { useUserStore } from '@/store/userStore';
  import { useOnboarding } from '@/composables/useOnboarding';
  import {
    openOnboardingChecklist,
    showOnboardingChecklistIfNeeded,
  } from '@/utils/showOnboardingChecklist';
  import Card from '@/components/partials/Card.vue';
  import Btn from '@/components/partials/Btn.vue';

  const CHECKLIST_STEP_KEYS = ['profile_complete', 'team_complete', 'products_complete', 'form_published'];

  export default {
    name: 'OnboardingChecklistBanner',

    components: {
      Card,
      Btn,
    },

    setup() {
      const route = useRoute();
      const userStore = useUserStore();
      const { needsChecklist, progress, fetchStatus, resetOnboardingStatus } = useOnboarding();

      const total = CHECKLIST_STEP_KEYS.length;

      const completedCount = computed(() => {
        const p = progress.value || {};
        return CHECKLIST_STEP_KEYS.filter((key) => p[key]).length;
      });

      const progressPercent = computed(() => {
        if (!total) return 0;
        return Math.round((completedCount.value / total) * 100);
      });

      const refreshStatus = async () => {
        try {
          await fetchStatus();
        } catch {
          // Non-blocking
        }
      };

      onMounted(async () => {
        await refreshStatus();
        await showOnboardingChecklistIfNeeded();
      });

      watch(
        () => route.fullPath,
        () => {
          refreshStatus();
        }
      );

      watch(
        () => userStore.currentOrganization?.id,
        async (orgId, previousOrgId) => {
          if (!orgId || orgId === previousOrgId) return;
          resetOnboardingStatus();
          await refreshStatus();
        }
      );

      const handleOpen = () => {
        openOnboardingChecklist();
      };

      return {
        needsChecklist,
        completedCount,
        total,
        progressPercent,
        handleOpen,
      };
    },
  };
</script>

<style scoped lang="scss">
  .onboarding-banner {
    margin-bottom: 1rem;

    &__content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;

      @include sm-down {
        flex-direction: column;
        align-items: stretch;
      }
    }

    &__title {
      font-weight: 600;
      color: var(--slate-900);
      margin-bottom: 0.25rem;
      @include fontSize('sm');
    }

    &__subtitle {
      color: var(--slate-500);
      margin-bottom: 0.5rem;
      @include fontSize('sm');
    }

    &__text {
      flex: 1;
      min-width: 0;
    }

    &__progress {
      height: 4px;
      background: var(--slate-100);
      border-radius: 999px;
      overflow: hidden;
      max-width: 280px;
    }

    &__progress-fill {
      height: 100%;
      background: var(--brand-500);
      border-radius: 999px;
      transition: width 0.2s ease;
    }
  }
</style>
